/**
 * Что посылка значит для игрока (ISSUE-118). Engine держит её столько тиков, сколько нужно на наибольшей скорости боя,
 * а на экране она живёт 7 с настоящего времени: при xN клиент ждёт в N раз больше тиков. Здесь — только эта
 * арифметика и аналитика, без DOM: посылка либо открыта (`parcelOpened`), либо исчезла, и это промах.
 */
import type { BattleSnapshot } from "@newfungame/contracts";
import { TICK_RATE } from "@newfungame/contracts";

/** Экран занят штабом и героями посередине: посылка уходит на край свободной части. */
const HQ_FROM = 280;
const HQ_TO = 720;

export interface ParcelSpot {
  /** Позиция в свободной части экрана, проценты. */
  left: number;
  top: number;
}

export interface LiveParcel extends ParcelSpot {
  id: number;
}

export interface ParcelToast extends ParcelSpot {
  /** Сквозной номер всплывашки: номера посылок в каждой волне начинаются с 1, ключ по ним не перезапускал анимацию. */
  seq: number;
  id: number;
  reward: "coins" | "boost";
  coins: number;
  boostId: string | null;
}

export interface ParcelView {
  live: LiveParcel | null;
  toast: ParcelToast | null;
}

export interface ParcelWatchDeps {
  lifeTicks: number;
  onTap(wave: number, reward: "coins" | "boost", seconds: number): void;
  onMissed(wave: number): void;
}

export interface ParcelWatch {
  /** Новый снимок и текущая скорость боя. Вид не менялся — тот же объект. */
  update(snap: BattleSnapshot, speed: number): ParcelView;
}

export function parcelSpot(spot: { x: number; y: number }): ParcelSpot {
  let { x } = spot;
  if (x > HQ_FROM && x < HQ_TO && spot.y > HQ_FROM && spot.y < HQ_TO) x = x < 500 ? HQ_FROM : HQ_TO;
  return { left: x / 10, top: spot.y / 10 };
}

export function createParcelWatch(deps: ParcelWatchDeps): ParcelWatch {
  let view: ParcelView = { live: null, toast: null };
  let current: { id: number; wave: number; startTick: number; hideAt: number; speed: number; spot: ParcelSpot } | null =
    null;
  let hiddenId = -1;
  let seq = 0;

  const set = (patch: Partial<ParcelView>): void => {
    view = { ...view, ...patch };
  };

  return {
    update(snap, speed) {
      const parcel = snap.parcels[0];
      const opened = snap.parcelOpened;
      if (opened && current?.id === opened.id && current.wave === snap.wave.number) {
        const seconds = Math.round((snap.frame - current.startTick) / (TICK_RATE * current.speed));
        deps.onTap(current.wave, opened.reward, seconds);
        set({
          live: null,
          toast: {
            ...current.spot,
            seq: ++seq,
            id: opened.id,
            reward: opened.reward,
            coins: opened.coins,
            boostId: opened.boostId,
          },
        });
        current = null;
      } else if (current && (parcel?.id !== current.id || snap.wave.number !== current.wave)) {
        deps.onMissed(current.wave);
        current = null;
        set({ live: null });
      }
      if (parcel && !current && parcel.id !== hiddenId) {
        const spot = parcelSpot(parcel.spot);
        const hideAt = parcel.startTick + deps.lifeTicks * speed;
        current = { id: parcel.id, wave: snap.wave.number, startTick: parcel.startTick, hideAt, speed, spot };
        set({ live: { id: parcel.id, ...spot } });
      }
      if (current && snap.frame >= current.hideAt) {
        hiddenId = current.id;
        deps.onMissed(current.wave);
        current = null;
        set({ live: null });
      }
      if (!parcel) hiddenId = -1;
      return view;
    },
  };
}
